import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { EmotionProjectReply } from "~/app/textToSpeech/project.service";

@Component({
  selector: "app-emotion-reply",
  templateUrl: "./emotion-reply.component.html",
  styleUrls: ["./emotion-reply.component.scss"],
})
export class EmotionReplyComponent implements OnChanges {
  private static readonly EMOTION_SUGGESTION_MAP: Record<string, string[]> = {
    angry: [
      "[angry]",
      "[furious]",
      "[enraged]",
      "[seething]",
      "[outraged]",
      "[irritated]",
      "[frustrated]",
      "[fuming]",
      "[bitter]",
      "[hostile]",
    ],
    disgusted: [
      "[disgusted]",
      "[revolted]",
      "[appalled]",
      "[repulsed]",
      "[contemptuous]",
      "[disdainful]",
      "[nauseated]",
      "[scornful]",
      "[with disgust]",
      "[grimacing]",
    ],
    fearful: [
      "[fearful]",
      "[terrified]",
      "[panicked]",
      "[nervously]",
      "[anxiously]",
      "[trembling]",
      "[petrified]",
      "[dreading]",
      "[with dread]",
      "[in a shaking voice]",
    ],
    happy: [
      "[happy]",
      "[joyful]",
      "[cheerfully]",
      "[excited]",
      "[delighted]",
      "[elated]",
      "[gleefully]",
      "[playfully]",
      "[enthusiastically]",
      "[warmly]",
    ],
    neutral: [
      "[neutral]",
      "[flatly]",
      "[calmly]",
      "[deadpan]",
      "[matter-of-factly]",
      "[evenly]",
      "[composed]",
      "[indifferent]",
      "[monotone]",
      "[stoic]",
    ],
    sad: [
      "[sad]",
      "[sorrowful]",
      "[tearfully]",
      "[mournful]",
      "[dejected]",
      "[heartbroken]",
      "[melancholic]",
      "[despondent]",
      "[forlorn]",
      "[quietly]",
    ],
    surprised: [
      "[surprised]",
      "[shocked]",
      "[astonished]",
      "[gasps]",
      "[stunned]",
      "[wide-eyed]",
      "[amazed]",
      "[bewildered]",
      "[startled]",
      "[taken aback]",
    ],
  };

  private static readonly EMOTION_CANONICAL_MAP: Record<string, string> = {
    anger: "angry",
    angry: "angry",
    furious: "angry",
    frustrated: "angry",
    irritated: "angry",
    hostility: "angry",
    hostile: "angry",
    disgust: "disgusted",
    disgusted: "disgusted",
    revolted: "disgusted",
    appalled: "disgusted",
    contempt: "disgusted",
    contemptuous: "disgusted",
    disdainful: "disgusted",
    fear: "fearful",
    fearful: "fearful",
    afraid: "fearful",
    terrified: "fearful",
    anxious: "fearful",
    nervous: "fearful",
    panic: "fearful",
    panicked: "fearful",
    happiness: "happy",
    happy: "happy",
    joy: "happy",
    joyful: "happy",
    excitement: "happy",
    excited: "happy",
    delight: "happy",
    delighted: "happy",
    neutral: "neutral",
    calm: "neutral",
    composed: "neutral",
    deadpan: "neutral",
    stoic: "neutral",
    sadness: "sad",
    sad: "sad",
    sorrowful: "sad",
    melancholy: "sad",
    melancholic: "sad",
    dejected: "sad",
    surprise: "surprised",
    surprised: "surprised",
    astonished: "surprised",
    shocked: "surprised",
    stunned: "surprised",
  };

  @Input() reply: EmotionProjectReply;
  @Input() index: number;
  @Input() editableDialogue = false;
  @Input() showEmotionLabels = false;
  @Input() isSaving = false;
  @Input() saveFailed = false;
  @Input() showSavedIndicator = false;
  @Input() playbackManagedExternally = false;
  @Input() externalIsPlaying = false;
  @Input() externalIsPlayingOriginal = false;
  @Input() playEnabled = true;
  @Input() originalPlayEnabled = false;
  @Input() stereoMixUrl: string | null = null;
  @Input() showOriginalAudioButton = false;
  @Input() formatSecondsToSmpte?: (sec: number) => string;
  @Output() saveDialogue = new EventEmitter<string>();
  @Output() playRequested = new EventEmitter<void>();
  @Output() playOriginalRequested = new EventEmitter<void>();
  @ViewChild("dialogueInput")
  private readonly dialogueInputRef?: ElementRef<HTMLTextAreaElement>;

  private currentAudio: HTMLAudioElement | null = null;
  private localSegmentTimeUpdate: (() => void) | null = null;
  private suppressNextBlurSave = false;
  private lastSelectionStart: number | null = null;
  private lastSelectionEnd: number | null = null;
  public isPlaying = false;
  private isPlayingOriginalLocal = false;
  public dialogueDraft = "";
  public activeEmotionLabel: string | null = null;

  public get playing(): boolean {
    return this.playbackManagedExternally
      ? this.externalIsPlaying
      : this.isPlaying;
  }

  public get playingOriginal(): boolean {
    return this.playbackManagedExternally
      ? this.externalIsPlayingOriginal
      : this.isPlayingOriginalLocal;
  }

  public get formattedCount(): string {
    const displayIndex = Number(this.index) + 1;
    return displayIndex < 10 ? `0${displayIndex}` : `${displayIndex}`;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.reply) {
      this.dialogueDraft =
        this.reply?.translatedDialogue ?? this.reply?.dialogue ?? "";
      this.activeEmotionLabel = null;
      this.lastSelectionStart = null;
      this.lastSelectionEnd = null;
    }
  }

  public onDialogueInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.dialogueDraft = target?.value ?? "";
    this.captureSelection(target);
  }

  public onDialogueSelectionChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.captureSelection(target);
  }

  public get hasDialogueChanges(): boolean {
    return (
      `${this.dialogueDraft}` !==
      `${this.reply?.translatedDialogue ?? this.reply?.dialogue ?? ""}`
    );
  }

  public get emotionLabels(): string[] {
    const raw = `${this.reply?.dominantEmotion ?? ""}`.trim();
    if (!raw) {
      return [];
    }

    return raw
      .split(/[,\|]/g)
      .map((label) => label.replace(/[[\]"']/g, "").trim())
      .filter((label) => !!label);
  }

  public get sentenceEmotionChips(): Array<{ text: string; emotion: string }> {
    const sentences = this.reply?.sentenceEmotions ?? [];
    return sentences
      .filter((sentence) => !!`${sentence?.emotion ?? ""}`.trim())
      .map((sentence) => ({
        text: `${sentence?.text ?? ""}`.trim(),
        emotion: `${sentence?.emotion ?? ""}`.trim(),
      }));
  }

  public get emotionSuggestions(): string[] {
    const key = this.normalizeEmotionKey(this.activeEmotionLabel);
    if (!key) {
      return [];
    }
    return EmotionReplyComponent.EMOTION_SUGGESTION_MAP[key] ?? [];
  }

  public getEmotionTagClass(label: string): string {
    const normalized = `${label || ""}`.trim().toLowerCase();

    if (
      ["happy", "joy", "excited", "love", "optimistic"].includes(normalized)
    ) {
      return "tag-deliver";
    }
    if (["sad", "grief", "melancholy"].includes(normalized)) {
      return "tag-share";
    }
    if (["angry", "anger", "frustrated", "furious"].includes(normalized)) {
      return "tag-publish";
    }
    if (["fear", "scared", "anxious", "nervous"].includes(normalized)) {
      return "tag-archive";
    }
    if (["surprised", "surprise"].includes(normalized)) {
      return "tag-find";
    }
    if (["disgust", "disgusted"].includes(normalized)) {
      return "tag-admin";
    }
    if (["neutral", "calm", "regular"].includes(normalized)) {
      return "tag-regular";
    }

    return "tag-find";
  }

  public saveDraft(): void {
    if (!this.hasDialogueChanges || this.isSaving) {
      return;
    }
    this.saveDialogue.emit(this.dialogueDraft);
  }

  public onEmotionTagMouseDown(): void {
    this.suppressNextBlurSave = true;
  }

  public onEmotionLabelClick(label: string): void {
    if (!this.editableDialogue) {
      return;
    }

    this.activeEmotionLabel = this.activeEmotionLabel === label ? null : label;
  }

  public onEmotionSuggestionClick(suggestion: string): void {
    if (!this.editableDialogue) {
      return;
    }

    this.dialogueDraft = this.insertSuggestionAtSelection(
      this.dialogueDraft,
      suggestion
    );
  }

  public onDialogueBlur(): void {
    if (!this.editableDialogue) {
      return;
    }
    if (this.suppressNextBlurSave) {
      this.suppressNextBlurSave = false;
      return;
    }
    this.saveDraft();
  }

  public onDialogueKeydown(event: KeyboardEvent): void {
    if (!this.editableDialogue || event.key !== "Enter" || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.suppressNextBlurSave = true;
    this.saveDraft();
  }

  public togglePlay(): void {
    if (this.playbackManagedExternally) {
      if (!this.playEnabled) {
        return;
      }
      this.playRequested.emit();
      return;
    }

    if (!this.reply?.audioFilePath) {
      return;
    }

    if (this.isPlaying && this.currentAudio) {
      this.stopLocalPlayback();
      return;
    }

    this.stopLocalPlayback();
    this.currentAudio = new Audio(this.reply.audioFilePath);
    this.isPlaying = true;
    this.currentAudio.addEventListener("ended", () => this.stopLocalPlayback());
    this.currentAudio.addEventListener("error", () => this.stopLocalPlayback());
    this.currentAudio.play().catch(() => this.stopLocalPlayback());
  }

  public togglePlayOriginal(): void {
    if (this.playbackManagedExternally) {
      if (!this.originalPlayEnabled) {
        return;
      }
      this.playOriginalRequested.emit();
      return;
    }

    const stereo = `${this.stereoMixUrl ?? ""}`.trim();
    if (!stereo || !this.reply) {
      return;
    }

    const start = Number(this.reply.startSec) || 0;
    const end = Number(this.reply.endSec) || 0;
    if (end <= start + 0.01) {
      return;
    }

    if (this.isPlayingOriginalLocal && this.currentAudio) {
      this.stopLocalPlayback();
      return;
    }

    this.stopLocalPlayback();
    const audio = new Audio(stereo);
    this.currentAudio = audio;
    this.isPlayingOriginalLocal = true;

    this.localSegmentTimeUpdate = () => {
      if (audio.currentTime >= end - 0.02) {
        audio.pause();
        this.stopLocalPlayback();
      }
    };

    const startPlayback = () => {
      try {
        audio.currentTime = start;
      } catch {
        // ignore seek errors
      }
      if (this.localSegmentTimeUpdate) {
        audio.addEventListener("timeupdate", this.localSegmentTimeUpdate);
      }
      audio.play().catch(() => this.stopLocalPlayback());
    };

    audio.addEventListener("ended", () => this.stopLocalPlayback());
    audio.addEventListener("error", () => this.stopLocalPlayback());

    if (audio.readyState >= 1) {
      startPlayback();
    } else {
      audio.addEventListener("loadeddata", startPlayback, { once: true });
    }
  }

  private stopLocalPlayback(): void {
    if (this.currentAudio && this.localSegmentTimeUpdate) {
      this.currentAudio.removeEventListener(
        "timeupdate",
        this.localSegmentTimeUpdate
      );
    }
    this.localSegmentTimeUpdate = null;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.isPlaying = false;
    this.isPlayingOriginalLocal = false;
  }

  private normalizeEmotionKey(label: string | null): string {
    const normalized = `${label || ""}`.trim().toLowerCase();
    if (!normalized) {
      return "";
    }
    return (
      EmotionReplyComponent.EMOTION_CANONICAL_MAP[normalized] ?? normalized
    );
  }

  private captureSelection(target: HTMLTextAreaElement | null): void {
    if (!target) {
      return;
    }
    this.lastSelectionStart = Number.isInteger(target.selectionStart)
      ? target.selectionStart
      : null;
    this.lastSelectionEnd = Number.isInteger(target.selectionEnd)
      ? target.selectionEnd
      : null;
  }

  private insertSuggestionAtSelection(
    dialogue: string,
    suggestion: string
  ): string {
    const currentDialogue = `${dialogue ?? ""}`;
    const cue = `${suggestion ?? ""}`.trim();

    if (!cue) {
      return currentDialogue;
    }
    if (!currentDialogue) {
      this.lastSelectionStart = cue.length;
      this.lastSelectionEnd = cue.length;
      return cue;
    }

    const canUseSelection =
      this.lastSelectionStart !== null &&
      this.lastSelectionEnd !== null &&
      this.lastSelectionStart >= 0 &&
      this.lastSelectionEnd >= this.lastSelectionStart &&
      this.lastSelectionEnd <= currentDialogue.length;

    if (canUseSelection) {
      const selectionStart = this.lastSelectionStart as number;
      const selectionEnd = this.lastSelectionEnd as number;
      const updated =
        currentDialogue.slice(0, selectionStart) +
        cue +
        currentDialogue.slice(selectionEnd);
      const nextCaret = selectionStart + cue.length;
      this.lastSelectionStart = nextCaret;
      this.lastSelectionEnd = nextCaret;
      this.restoreCaret(nextCaret);
      return updated;
    }

    const updated = `${cue} ${currentDialogue}`.trim();
    this.lastSelectionStart = cue.length;
    this.lastSelectionEnd = cue.length;
    return updated;
  }

  private restoreCaret(position: number): void {
    if (!this.dialogueInputRef?.nativeElement) {
      return;
    }
    setTimeout(() => {
      const textarea = this.dialogueInputRef?.nativeElement;
      if (!textarea) {
        return;
      }
      textarea.focus();
      textarea.setSelectionRange(position, position);
    });
  }
}
